import { useCallback, useEffect, useId, useLayoutEffect, useRef, type InputHTMLAttributes } from 'react';
import {
  usePouchValueKeyboard,
  type PouchValueKeyboardSession,
} from '../context/PouchValueKeyboardContext';
import {
  backspaceAtInputSelection,
  filterPouchValueInput,
  insertTextAtInputSelection,
  type PouchValueKeyboardVariant,
} from '../utils/pouchValueInput';

export interface UsePouchValueKeyboardBindingOptions {
  active: boolean;
  isTouchDevice: boolean;
  /** キーボード出現位置の自動調整用（キャンバス上のパウチ） */
  pouchId?: string;
  variant: PouchValueKeyboardVariant;
  isTime?: boolean;
  value: string;
  onValueChange: (value: string) => void;
  onConfirm: () => void;
  onCancel?: () => void;
  /** パウチ値編集時のみ: 値確定してタイトル編集へ */
  onGoToTitle?: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

export function usePouchValueKeyboardBinding({
  active,
  isTouchDevice,
  pouchId,
  variant,
  isTime = false,
  value,
  onValueChange,
  onConfirm,
  onCancel,
  onGoToTitle,
  inputRef,
}: UsePouchValueKeyboardBindingOptions) {
  const sessionId = useId();
  const ctx = usePouchValueKeyboard();
  const valueRef = useRef(value);
  valueRef.current = value;

  const onValueChangeRef = useRef(onValueChange);
  const onConfirmRef = useRef(onConfirm);
  const onCancelRef = useRef(onCancel);
  const onGoToTitleRef = useRef(onGoToTitle);
  onValueChangeRef.current = onValueChange;
  onConfirmRef.current = onConfirm;
  onCancelRef.current = onCancel;
  onGoToTitleRef.current = onGoToTitle;

  const applyFilteredValue = useCallback(
    (raw: string, caret?: number) => {
      let filtered = filterPouchValueInput(raw, variant, isTime);
      if (variant === 'expression' && filtered.includes('...')) {
        filtered = filtered.replace(/\.{3,}/g, '');
      }
      onValueChangeRef.current(filtered);
      if (caret !== undefined && inputRef.current) {
        const pos = Math.min(caret, filtered.length);
        requestAnimationFrame(() => {
          const el = inputRef.current;
          if (!el) return;
          try {
            el.setSelectionRange(pos, pos);
          } catch {
            /* ignore */
          }
        });
      }
    },
    [variant, isTime, inputRef],
  );

  const insertText = useCallback(
    (text: string) => {
      const el = inputRef.current;
      const cur = valueRef.current;
      if (!el) {
        applyFilteredValue(cur + text);
        return;
      }
      const { value: joined, caret } = insertTextAtInputSelection(el, cur, text);
      applyFilteredValue(joined, caret);
    },
    [applyFilteredValue, inputRef],
  );

  const backspace = useCallback(() => {
    const el = inputRef.current;
    const cur = valueRef.current;
    if (!el) {
      if (cur.length > 0) {
        applyFilteredValue(cur.slice(0, -1));
      }
      return;
    }
    const result = backspaceAtInputSelection(el, cur);
    if (!result) return;
    applyFilteredValue(result.value, result.caret);
  }, [applyFilteredValue, inputRef]);

  const insertTextRef = useRef(insertText);
  const backspaceRef = useRef(backspace);
  insertTextRef.current = insertText;
  backspaceRef.current = backspace;

  useLayoutEffect(() => {
    if (!ctx || !active || !isTouchDevice) {
      ctx?.unregisterSession(sessionId);
      return;
    }

    const session: PouchValueKeyboardSession = {
      id: sessionId,
      pouchId,
      variant,
      isTime,
      insertText: (text: string) => insertTextRef.current(text),
      backspace: () => backspaceRef.current(),
      confirm: () => onConfirmRef.current(),
      cancel: onCancelRef.current ? () => onCancelRef.current?.() : undefined,
      goToTitle: onGoToTitleRef.current ? () => onGoToTitleRef.current?.() : undefined,
    };
    ctx.registerSession(session);
    return () => {
      ctx.unregisterSession(sessionId);
    };
  }, [ctx, active, isTouchDevice, sessionId, pouchId, variant, isTime, !!onGoToTitle]);

  const hardwareActive = ctx?.hardwareKeyboardActive ?? false;
  const showCustomKeyboard = isTouchDevice && active && !hardwareActive;

  return {
    hardwareKeyboardActive: hardwareActive,
    showCustomKeyboard,
    /** PC、またはタッチ+外付けキーボード時は keydown 直挿入を有効にする */
    useDirectKeyInsert: !isTouchDevice || hardwareActive,
    inputProps: {
      inputMode: 'none' as const,
      ...(isTouchDevice && active
        ? ({ virtualKeyboardPolicy: 'manual' } as InputHTMLAttributes<HTMLInputElement>)
        : {}),
    },
  };
}
